// 참가자 도메인 시트 — 운영진이 일정 상세에서 여는 두 시트.
//
//  - openAddParticipantSheet: 회원/게스트 두 탭으로 add/remove를 한 번에.
//    회원은 desiredMemberIds 다이얼, 게스트는 currentGuests/removedGuestIds +
//    guestQueue로 추가/삭제를 큐잉했다가 저장 시 한 번에 API 호출.
//  - openParticipantAvailabilitySheet: 늦참/조퇴 분 단위 조정. 일정 시간 외에는
//    스케줄러가 자동 제외.
//
// 두 시트 모두 저장 후 화면 갱신은 호출자의 rerender 콜백으로.

import { Fragment } from "preact";
import { useLayoutEffect, useState } from "preact/hooks";
import { api } from "../api";
import { canSeeNtrp } from "../auth";
import { matchPlayerName } from "../matches";
import { navigate } from "../routes";
import { fetchMembers } from "../state";
import { Icon } from "../ui/components/Icon";
import { Segment } from "../ui/components/Segment";
import {
  formatNtrp,
  formatTimeShort,
  genderLabel,
  isoMinuteDiff,
  shiftIsoByMinutes,
} from "../ui/format";
import { mountPreact } from "../ui/preact-mount";
import { closeSheet, openSheet, toast } from "../ui/sheet";
import { matchName } from "../ui/text";

export interface ParticipantSheetOpts {
  rerender: () => void;
}

// ---- shared store helper (body + footer 두 트리에서 같은 상태 공유) ----
interface Store<T> {
  get: () => T;
  set: (updater: (prev: T) => T) => void;
  subscribe: (fn: () => void) => () => void;
}

function createStore<T>(initial: T): Store<T> {
  let value = initial;
  const subs = new Set<() => void>();
  return {
    get: () => value,
    set: (updater) => {
      value = updater(value);
      subs.forEach((fn) => fn());
    },
    subscribe: (fn) => { subs.add(fn); return () => { subs.delete(fn); }; },
  };
}

function useStore<T>(store: Store<T>): T {
  const [, setTick] = useState(0);
  // useEffect는 Preact에서 rAF 뒤에 실행되므로, mountPreact 직후 호출되는
  // 동기 fetch가 구독 등록 전에 store.set을 쳐서 알림이 유실되는 race 발생.
  // useLayoutEffect로 commit 직후 동기 등록.
  useLayoutEffect(() => store.subscribe(() => setTick((t) => t + 1)), []);
  return store.get();
}

// =====================================================================
// openAddParticipantSheet
// =====================================================================

interface AddParticipantState {
  tab: "member" | "guest";
  desiredMemberIds: Set<string>;
  removedGuestIds: Set<string>;
  guestQueue: any[];
  members: any[] | null;
  membersError: string | null;
  saving: boolean;
  search: string;
  guestName: string;
  guestGender: "male" | "female";
  guestNtrp: number | null;
  // 인라인 게스트 편집 — 한 번에 하나만. ✏️ 클릭 시 id 세팅, 저장/취소 시 null.
  // 이름·성별·NTRP 모두 같은 모드에서 편집 (M6 "내 모임"처럼 점진적으로 채우는
  // 흐름에 맞춰 한 화면에서 일괄 수정).
  editingGuestId: string | null;
  editingGuestName: string;
  editingGuestGender: "male" | "female" | null;
  editingGuestNtrp: number | null;
  // canManage 호스트 흐름은 큐잉 패턴 — ✏️ "저장"이 즉시 PATCH 하지 않고 여기에
  // commit. footer "저장" 클릭 시 추가/삭제와 함께 일괄 PATCH. 시트가 닫히면
  // (onSave 안 거치고 X/ESC) 큐는 폐기 = "저장 안 함" 의도.
  // canRename only(비호스트의 정기모임 게스트 이름 수정)는 footer 자체가 없어서
  // 큐잉 못 함 → 기존처럼 즉시 PATCH 흐름 유지 (이 Map은 사용 안 됨).
  pendingGuestEdits: Map<string, PendingGuestEdit>;
  // canRename only 즉시 PATCH 후 시트 닫힐 때 부모 rerender 트리거. canManage
  // 흐름에선 onSave가 직접 rerender 부르므로 무관.
  renameDirty: boolean;
}

interface PendingGuestEdit {
  display_name: string;
  gender_snapshot: "male" | "female" | null;
  ntrp_snapshot: number | null;
}

const NTRP_MIN = 1.0, NTRP_MAX = 7.0, NTRP_STEP = 0.5;
const clampNtrp = (v: number) => Math.max(NTRP_MIN, Math.min(NTRP_MAX, Math.round(v * 2) / 2));

function MemberPane({
  store,
  alreadyIds,
  onReload,
}: {
  store: Store<AddParticipantState>;
  alreadyIds: Set<string>;
  onReload: () => void;
}) {
  const s = useStore(store);
  if (s.members === null && !s.membersError) {
    return <div class="loading" style="padding:32px 0;">회원 불러오는 중…</div>;
  }
  if (s.membersError) {
    return (
      <div class="card empty">
        {s.membersError}
        <div class="mt-3">
          <button class="btn btn--ghost btn--sm" type="button" onClick={onReload}>다시 시도</button>
        </div>
      </div>
    );
  }
  const members = s.members || [];
  if (members.length === 0) {
    return (
      <div class="card empty">
        회원 목록이 비어있어요.
        <div class="mt-3">
          <button
            class="btn btn--primary btn--sm"
            type="button"
            onClick={() => { closeSheet(); navigate({ name: "members" }); }}
          >
            회원 관리로 가기
          </button>
        </div>
      </div>
    );
  }

  const query = s.search.trim().toLowerCase();
  const filtered = query ? members.filter((m) => matchName(m.name, query)) : members;
  const males = filtered.filter((m) => m.gender === "male");
  const females = filtered.filter((m) => m.gender !== "male");

  const toAddCount = [...s.desiredMemberIds].filter((id) => !alreadyIds.has(id)).length;
  const toRemoveCount = [...alreadyIds].filter((id) => !s.desiredMemberIds.has(id)).length;
  const infoParts = [`참가 중 ${alreadyIds.size}명`];
  if (toAddCount > 0) infoParts.push(`추가 +${toAddCount}`);
  if (toRemoveCount > 0) infoParts.push(`삭제 -${toRemoveCount}`);

  function rowClass(mid: string) {
    const isInitial = alreadyIds.has(mid);
    const isDesired = s.desiredMemberIds.has(mid);
    if (isInitial && isDesired) return "list__item list__item--participant";
    if (!isInitial && isDesired) return "list__item list__item--selected";
    if (isInitial && !isDesired) return "list__item list__item--removing";
    return "list__item";
  }

  function toggleMember(mid: string) {
    store.set((prev) => {
      const next = new Set(prev.desiredMemberIds);
      if (next.has(mid)) next.delete(mid);
      else next.add(mid);
      return { ...prev, desiredMemberIds: next };
    });
  }

  function renderRow(m: any) {
    const meta = [genderLabel(m.gender), canSeeNtrp() && m.ntrp ? ` · NTRP ${formatNtrp(m.ntrp)}` : ""].filter(Boolean).join("");
    return (
      <button
        key={m.member_id}
        type="button"
        class={rowClass(m.member_id)}
        onClick={() => toggleMember(m.member_id)}
      >
        <div class="list__avatar">{(m.name || "?").slice(0, 1)}</div>
        <div class="list__main">
          <p class="list__title">{m.name}</p>
          <p class="list__meta">{meta}</p>
        </div>
      </button>
    );
  }

  return (
    <Fragment>
      <input
        class="input input--compact"
        type="text"
        placeholder="이름으로 검색…"
        autocomplete="off"
        style="margin-bottom:8px"
        value={s.search}
        onInput={(e) => store.set((p) => ({ ...p, search: (e.currentTarget as HTMLInputElement).value }))}
      />
      <div class="toolbar">
        <div class="toolbar__info">{infoParts.join(" · ")}</div>
      </div>
      <div class="list list--compact">
        {males.length > 0 && <div class="gender-label">남 {males.length}명</div>}
        {males.map(renderRow)}
        {females.length > 0 && <div class="gender-label">여 {females.length}명</div>}
        {females.map(renderRow)}
        {filtered.length === 0 && query && (
          <div class="card empty">"{query}" 검색 결과가 없어요</div>
        )}
      </div>
    </Fragment>
  );
}

function GuestPane({
  store,
  currentGuests,
  eventId,
  canRename,
  canManage,
  hideGuestSuffix,
}: {
  store: Store<AddParticipantState>;
  currentGuests: any[];
  eventId: string;
  // 정기모임은 항상 true(자유도 정책), 특별전은 canManage 만 true로 들어옴.
  canRename: boolean;
  // 게스트 추가/삭제는 호스트/운영진만. false면 슬림 모드(리스트 + ✏️ 만).
  canManage: boolean;
  // personal "내 모임"은 명단 전원이 외부인이라 게스트 표기 의미 없음.
  hideGuestSuffix?: boolean;
}) {
  const s = useStore(store);

  function toggleGuestRemoval(pid: string) {
    if (!canManage) return; // 슬림 모드에선 row 탭 = no-op
    // 인라인 이름 수정 중에는 row 탭이 삭제 토글로 빠지지 않게 막음.
    if (store.get().editingGuestId) return;
    store.set((prev) => {
      const next = new Set(prev.removedGuestIds);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return { ...prev, removedGuestIds: next };
    });
  }

  function startRename(gp: any) {
    // pending 변경이 이미 있으면 그 값으로 prefill — "다시 편집" 시 마지막 입력
    // 유지가 자연스러움. 사용자가 원래 값으로 되돌리면 saveRename에서 pending
    // 자동 제거.
    const pending = store.get().pendingGuestEdits.get(gp.event_participant_id);
    const baseName = pending?.display_name ?? gp.display_name ?? "";
    const rawGender = pending ? pending.gender_snapshot : gp.gender_snapshot;
    const baseGender = rawGender === "male" || rawGender === "female" ? rawGender : null;
    const baseNtrp = pending ? pending.ntrp_snapshot : (gp.ntrp_snapshot ?? null);
    store.set((prev) => ({
      ...prev,
      editingGuestId: gp.event_participant_id,
      editingGuestName: baseName,
      editingGuestGender: baseGender,
      editingGuestNtrp: baseNtrp,
    }));
  }

  function cancelRename() {
    store.set((prev) => ({
      ...prev,
      editingGuestId: null,
      editingGuestName: "",
      editingGuestGender: null,
      editingGuestNtrp: null,
    }));
  }

  function bumpEditingNtrpDown() {
    store.set((prev) => {
      if (prev.editingGuestNtrp === null) return prev;
      const next = prev.editingGuestNtrp <= NTRP_MIN ? null : clampNtrp(prev.editingGuestNtrp - NTRP_STEP);
      return { ...prev, editingGuestNtrp: next };
    });
  }

  function bumpEditingNtrpUp() {
    store.set((prev) => {
      const next = prev.editingGuestNtrp === null
        ? (prev.editingGuestGender === "female" ? 2.0 : 3.0)
        : clampNtrp(prev.editingGuestNtrp + NTRP_STEP);
      return { ...prev, editingGuestNtrp: next };
    });
  }

  function toggleEditingGender() {
    store.set((prev) => {
      const next = prev.editingGuestGender === "male"
        ? "female"
        : prev.editingGuestGender === "female"
          ? null
          : "male";
      return { ...prev, editingGuestGender: next };
    });
  }

  async function saveRename(gp: any) {
    const s = store.get();
    const newName = s.editingGuestName.trim();
    if (!newName) { toast("이름을 입력해주세요"); return; }
    const newGender = s.editingGuestGender;
    const newNtrp = s.editingGuestNtrp;

    if (canManage) {
      // 큐잉 패턴 — store.pendingGuestEdits에 commit. 실제 PATCH는 footer "저장"이
      // 추가/삭제와 함께 일괄 처리.
      const originalGender = gp.gender_snapshot === "male" || gp.gender_snapshot === "female"
        ? gp.gender_snapshot
        : null;
      const originalName = gp.display_name || "";
      const originalNtrp = gp.ntrp_snapshot ?? null;
      const sameAsOriginal =
        newName === originalName &&
        newGender === originalGender &&
        newNtrp === originalNtrp;
      store.set((prev) => {
        const next = new Map(prev.pendingGuestEdits);
        if (sameAsOriginal) {
          // 원래 값으로 되돌렸으면 pending 제거 (revert).
          next.delete(gp.event_participant_id);
        } else {
          next.set(gp.event_participant_id, {
            display_name: newName,
            gender_snapshot: newGender,
            ntrp_snapshot: newNtrp,
          });
        }
        return {
          ...prev,
          pendingGuestEdits: next,
          editingGuestId: null,
          editingGuestName: "",
          editingGuestGender: null,
          editingGuestNtrp: null,
        };
      });
      return;
    }

    // canRename only(비호스트의 정기모임 게스트 이름 수정): 즉시 PATCH 유지.
    // 백엔드의 is_guest_rename_only 분기 통과 위해 display_name만 보냄.
    if (newName === gp.display_name) { cancelRename(); return; }
    try {
      await api(`/api/v1/events/${eventId}/participants/${gp.event_participant_id}`, {
        method: "PATCH",
        body: { display_name: newName },
      });
      gp.display_name = newName;
      store.set((prev) => ({
        ...prev,
        editingGuestId: null,
        editingGuestName: "",
        editingGuestGender: null,
        editingGuestNtrp: null,
        renameDirty: true,
      }));
      toast("이름을 바꿨어요");
    } catch (err: any) {
      toast(err.message || "저장에 실패했어요");
    }
  }

  function bumpNtrpDown() {
    store.set((prev) => {
      if (prev.guestNtrp === null) return prev;
      const next = prev.guestNtrp <= NTRP_MIN ? null : clampNtrp(prev.guestNtrp - NTRP_STEP);
      return { ...prev, guestNtrp: next };
    });
  }

  function bumpNtrpUp() {
    store.set((prev) => {
      const next = prev.guestNtrp === null
        ? (prev.guestGender === "female" ? 2.0 : 3.0)
        : clampNtrp(prev.guestNtrp + NTRP_STEP);
      return { ...prev, guestNtrp: next };
    });
  }

  function addGuest() {
    const name = s.guestName.trim();
    if (!name) { toast("이름을 입력해주세요"); return; }
    store.set((prev) => ({
      ...prev,
      guestQueue: [...prev.guestQueue, { display_name: name, gender_snapshot: prev.guestGender, ntrp_snapshot: prev.guestNtrp }],
      guestName: "",
    }));
  }

  function removeQueued(idx: number) {
    store.set((prev) => ({
      ...prev,
      guestQueue: prev.guestQueue.filter((_, i) => i !== idx),
    }));
  }

  return (
    <Fragment>
      {canManage && (
        <div class="guest-input-row">
          <input
            class="input input--compact"
            type="text"
            placeholder="이름"
            autocomplete="off"
            value={s.guestName}
            onInput={(e) => store.set((p) => ({ ...p, guestName: (e.currentTarget as HTMLInputElement).value }))}
          />
          <button
            type="button"
            class="btn btn--ghost btn--sm guest-toggle"
            onClick={() => store.set((p) => ({ ...p, guestGender: p.guestGender === "male" ? "female" : "male" }))}
          >
            {s.guestGender === "male" ? "남" : "여"}
          </button>
          <div class="stepper stepper--sm">
            <button type="button" class="stepper__btn stepper__btn--sm" onClick={bumpNtrpDown}>
              <Icon name="minus" size={14} />
            </button>
            <span class="guest-ntrp-val">{s.guestNtrp === null ? "모름" : s.guestNtrp.toFixed(1)}</span>
            <button type="button" class="stepper__btn stepper__btn--sm" onClick={bumpNtrpUp}>
              <Icon name="plus" size={14} />
            </button>
          </div>
          <button type="button" class="btn btn--primary btn--sm" onClick={addGuest}>추가</button>
        </div>
      )}
      {!canManage && (
        <p class="muted small" style="margin:0 0 4px">
          게스트 이름이 잘못됐다면 ✏️ 로 바꿔주세요. 추가·삭제는 호스트가 합니다.
        </p>
      )}
      <div class="list list--compact">
        {currentGuests.map((gp) => {
          const isRemoving = s.removedGuestIds.has(gp.event_participant_id);
          const isEditing = s.editingGuestId === gp.event_participant_id;
          // canManage 큐잉 흐름 — pending 있으면 그 값을 effective로 표시.
          // 실제 데이터(gp)는 footer 저장 시 PATCH 후 부모 rerender로 갱신됨.
          const pending = s.pendingGuestEdits.get(gp.event_participant_id);
          const isPending = !!pending && !isRemoving && !isEditing;
          const effName = pending?.display_name ?? gp.display_name ?? "";
          const effGender = pending ? pending.gender_snapshot : gp.gender_snapshot;
          const effNtrp = pending ? pending.ntrp_snapshot : gp.ntrp_snapshot;
          const meta = [
            genderLabel(effGender),
            effNtrp ? ` · NTRP ${formatNtrp(effNtrp)}` : "",
            isPending ? " · 저장 대기" : "",
          ].filter(Boolean).join("");
          if (isEditing) {
            const genderLabelText = s.editingGuestGender === "male"
              ? "남"
              : s.editingGuestGender === "female"
                ? "여"
                : "?";
            // 펼침 폼 레이아웃: 모바일 시트 너비에 맞춰 의도적 두 줄.
            // - 첫 줄: avatar + 이름 input
            // - 둘째 줄(canManage): 성별 토글 + NTRP stepper + 적용/취소
            // canRename only(비호스트)는 성별/NTRP UI 없어 첫 줄에 적용/취소까지.
            //
            // 단어 분리: 인라인 "적용" = 큐에 반영(임시), footer "저장" = 서버 PATCH.
            return (
              <div
                key={gp.event_participant_id}
                class="list__item list__item--participant"
                style="display:flex;flex-direction:column;align-items:stretch;gap:6px"
              >
                <div style="display:flex;align-items:center;gap:6px">
                  <div class="list__avatar">{(s.editingGuestName || "?").slice(0, 1)}</div>
                  <input
                    class="input input--compact"
                    type="text"
                    autocomplete="off"
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                    value={s.editingGuestName}
                    onInput={(e) => {
                      const v = (e.currentTarget as HTMLInputElement).value;
                      store.set((p) => ({ ...p, editingGuestName: v }));
                    }}
                    onKeyDown={(e: KeyboardEvent) => {
                      if (e.key === "Enter") { e.preventDefault(); saveRename(gp); }
                      else if (e.key === "Escape") { e.preventDefault(); cancelRename(); }
                    }}
                    style="flex:1;min-width:0"
                  />
                  {!canManage && (
                    <Fragment>
                      <button type="button" class="btn btn--primary btn--sm" onClick={() => saveRename(gp)}>적용</button>
                      <button
                        type="button"
                        class="btn btn--ghost btn--sm"
                        aria-label="취소"
                        title="취소"
                        onClick={cancelRename}
                      >
                        <Icon name="x" size={14} />
                      </button>
                    </Fragment>
                  )}
                </div>
                {canManage && (
                  <div style="display:flex;align-items:center;gap:6px">
                    <button
                      type="button"
                      class="btn btn--ghost btn--sm guest-toggle"
                      aria-label="성별"
                      title="남 ↔ 여 ↔ 모름"
                      onClick={toggleEditingGender}
                    >
                      {genderLabelText}
                    </button>
                    <div class="stepper stepper--sm">
                      <button type="button" class="stepper__btn stepper__btn--sm" onClick={bumpEditingNtrpDown}>
                        <Icon name="minus" size={14} />
                      </button>
                      <span class="guest-ntrp-val">{s.editingGuestNtrp === null ? "모름" : s.editingGuestNtrp.toFixed(1)}</span>
                      <button type="button" class="stepper__btn stepper__btn--sm" onClick={bumpEditingNtrpUp}>
                        <Icon name="plus" size={14} />
                      </button>
                    </div>
                    <button
                      type="button"
                      class="btn btn--primary btn--sm"
                      style="margin-left:auto"
                      onClick={() => saveRename(gp)}
                    >
                      적용
                    </button>
                    <button
                      type="button"
                      class="btn btn--ghost btn--sm"
                      aria-label="취소"
                      title="취소"
                      onClick={cancelRename}
                    >
                      <Icon name="x" size={14} />
                    </button>
                  </div>
                )}
              </div>
            );
          }
          return (
            <div
              key={gp.event_participant_id}
              class={"list__item " + (isRemoving ? "list__item--removing" : "list__item--participant")}
              style={"display:flex;align-items:center" + (isPending ? ";border-left:3px solid var(--accent, #16a34a)" : "")}
            >
              <button
                type="button"
                onClick={() => toggleGuestRemoval(gp.event_participant_id)}
                style="flex:1;display:flex;align-items:center;background:none;border:0;padding:0;text-align:left;color:inherit;cursor:pointer"
              >
                <div class="list__avatar">{(effName || "?").slice(0, 1)}</div>
                <div class="list__main">
                  <p class="list__title">{effName ? (hideGuestSuffix ? effName : `${effName}(게)`) : "(이름 없음)"}</p>
                  <p class="list__meta">{meta}</p>
                </div>
              </button>
              {canRename && (
                <button
                  type="button"
                  class="btn btn--ghost btn--sm"
                  aria-label="이름 수정"
                  title="이름 수정"
                  onClick={(e: Event) => { e.stopPropagation(); startRename(gp); }}
                  style="margin-left:6px"
                >
                  <Icon name="edit" size={14} />
                </button>
              )}
            </div>
          );
        })}
        {s.guestQueue.map((g, idx) => (
          <div key={`q-${idx}`} class="list__item list__item--selected">
            <div class="list__avatar">{g.display_name.slice(0, 1)}</div>
            <div class="list__main">
              <p class="list__title">{hideGuestSuffix ? g.display_name : `${g.display_name}(게)`}</p>
              <p class="list__meta">
                {genderLabel(g.gender_snapshot)}{g.ntrp_snapshot != null ? ` · NTRP ${formatNtrp(g.ntrp_snapshot)}` : " · NTRP 모름"}
              </p>
            </div>
            <button
              class="list__delete"
              type="button"
              aria-label="취소"
              onClick={(e: Event) => { e.stopPropagation(); removeQueued(idx); }}
            >
              <Icon name="x" size={16} />
            </button>
          </div>
        ))}
      </div>
    </Fragment>
  );
}

function AddParticipantBody({
  store,
  alreadyIds,
  currentGuests,
  eventId,
  canRename,
  canManage,
  hideGuestSuffix,
  onReload,
}: {
  store: Store<AddParticipantState>;
  alreadyIds: Set<string>;
  currentGuests: any[];
  eventId: string;
  canRename: boolean;
  canManage: boolean;
  hideGuestSuffix?: boolean;
  onReload: () => void;
}) {
  const s = useStore(store);
  // 비호스트 회원 슬림 모드: 회원 탭 / 추가·삭제 UI 숨기고 게스트 리스트 + ✏️ 만.
  if (!canManage) {
    return (
      <div class="stack">
        <GuestPane
          store={store}
          currentGuests={currentGuests}
          eventId={eventId}
          canRename={canRename}
          canManage={false}
          hideGuestSuffix={hideGuestSuffix}
        />
      </div>
    );
  }
  return (
    <div class="stack">
      <Segment
        options={[["member", "회원"], ["guest", "게스트"]]}
        initial={s.tab}
        onChange={(val: "member" | "guest") => store.set((p) => ({ ...p, tab: val }))}
      />
      <div class="tab-pane" hidden={s.tab !== "member"}>
        <MemberPane store={store} alreadyIds={alreadyIds} onReload={onReload} />
      </div>
      <div class="tab-pane" hidden={s.tab !== "guest"}>
        <GuestPane store={store} currentGuests={currentGuests} eventId={eventId} canRename={canRename} canManage hideGuestSuffix={hideGuestSuffix} />
      </div>
    </div>
  );
}

function AddParticipantFooter({
  store,
  alreadyIds,
  onSave,
}: {
  store: Store<AddParticipantState>;
  alreadyIds: Set<string>;
  onSave: () => void;
}) {
  const s = useStore(store);
  const addedN = [...s.desiredMemberIds].filter((id) => !alreadyIds.has(id)).length + s.guestQueue.length;
  const removedN = [...alreadyIds].filter((id) => !s.desiredMemberIds.has(id)).length + s.removedGuestIds.size;
  const editedN = s.pendingGuestEdits.size;
  let label: string;
  let disabled: boolean;
  if (s.saving) { label = "저장 중…"; disabled = true; }
  else if (addedN === 0 && removedN === 0 && editedN === 0) {
    label = "변경사항 없음";
    disabled = true;
  }
  else {
    // 추가/삭제와 인라인 수정을 모두 한 번의 footer 저장으로 일괄 PATCH 한다.
    // 라벨엔 카운트 분류 그대로 노출 — "+추가 -삭제 ✏️수정".
    const parts: string[] = [];
    if (addedN > 0) parts.push(`+${addedN}`);
    if (removedN > 0) parts.push(`-${removedN}`);
    if (editedN > 0) parts.push(`✏️${editedN}`);
    label = `저장 (${parts.join(" ")})`;
    disabled = false;
  }
  return (
    <button class="btn btn--accent btn--block" type="button" disabled={disabled} onClick={onSave}>
      {label}
    </button>
  );
}

export async function openAddParticipantSheet(
  eventId: string,
  existingParticipants: any[] = [],
  opts: ParticipantSheetOpts & { ev?: any; canManage?: boolean },
): Promise<void> {
  const { rerender, ev, canManage } = opts;
  const isRegular = ev?.event_type === "regular";
  // 정기모임은 게스트 이름 수정을 활성 회원 누구나 OK (자유도 정책).
  // 특별전은 호스트/운영진(canManage)만.
  const canRename = isRegular ? true : !!canManage;
  const alreadyIds = new Set<string>(
    (existingParticipants || []).map((p) => p.member_id).filter(Boolean),
  );
  const participantByMemberId = new Map<string, any>();
  for (const p of existingParticipants || []) {
    if (p.member_id) participantByMemberId.set(p.member_id, p);
  }
  const currentGuests = (existingParticipants || []).filter((p) => !p.member_id);

  const store = createStore<AddParticipantState>({
    tab: "member",
    desiredMemberIds: new Set(alreadyIds),
    removedGuestIds: new Set(),
    guestQueue: [],
    members: null,
    membersError: null,
    saving: false,
    search: "",
    guestName: "",
    guestGender: "male",
    guestNtrp: null,
    editingGuestId: null,
    editingGuestName: "",
    editingGuestGender: null,
    editingGuestNtrp: null,
    pendingGuestEdits: new Map(),
    renameDirty: false,
  });

  async function loadMembers(force = false) {
    store.set((p) => ({ ...p, members: null, membersError: null }));
    try {
      const members = await fetchMembers(force ? { force: true } : undefined);
      store.set((p) => ({ ...p, members }));
    } catch (err: any) {
      store.set((p) => ({ ...p, membersError: err.message || "회원 목록을 불러올 수 없어요." }));
    }
  }

  async function onSave() {
    const s = store.get();
    const toAdd = [...s.desiredMemberIds].filter((id) => !alreadyIds.has(id));
    const toRemove = [...alreadyIds].filter((id) => !s.desiredMemberIds.has(id));
    const guestRemovals = [...s.removedGuestIds];
    const newGuests = [...s.guestQueue];
    const guestEdits = [...s.pendingGuestEdits.entries()];
    if (
      toAdd.length === 0 &&
      toRemove.length === 0 &&
      guestRemovals.length === 0 &&
      newGuests.length === 0 &&
      guestEdits.length === 0
    ) {
      closeSheet();
      return;
    }
    store.set((p) => ({ ...p, saving: true }));
    try {
      if (toAdd.length > 0) {
        await api(`/api/v1/events/${eventId}/participants/members`, {
          method: "POST",
          body: { member_ids: toAdd },
        });
      }
      for (const mid of toRemove) {
        const p = participantByMemberId.get(mid);
        if (p) await api(`/api/v1/events/${eventId}/participants/${p.event_participant_id}`, { method: "DELETE" });
      }
      for (const pid of guestRemovals) {
        await api(`/api/v1/events/${eventId}/participants/${pid}`, { method: "DELETE" });
      }
      for (const g of newGuests) {
        await api(`/api/v1/events/${eventId}/participants/guests`, { method: "POST", body: g });
      }
      // 큐잉된 인라인 수정 — 이름·성별·NTRP를 한 번에 PATCH (백엔드의
      // is_guest_rename_only 분기는 안 타지만 canManage 흐름이라 staff 권한 통과).
      for (const [pid, edit] of guestEdits) {
        await api(`/api/v1/events/${eventId}/participants/${pid}`, {
          method: "PATCH",
          body: {
            display_name: edit.display_name,
            gender_snapshot: edit.gender_snapshot,
            ntrp_snapshot: edit.ntrp_snapshot,
          },
        });
      }
      closeSheet();
      const addedN = toAdd.length + newGuests.length;
      const removedN = toRemove.length + guestRemovals.length;
      const editedN = guestEdits.length;
      const parts: string[] = [];
      if (addedN > 0) parts.push(`${addedN}명 추가`);
      if (removedN > 0) parts.push(`${removedN}명 삭제`);
      if (editedN > 0) parts.push(`${editedN}명 수정`);
      toast(parts.join(" · "));
      rerender();
    } catch (err: any) {
      store.set((p) => ({ ...p, saving: false }));
      toast(err.message);
    }
  }

  // personal "내 모임"은 명단 전원이 외부인이라 게스트 표기를 모두에게 붙일 의미 없음.
  const hideGuestSuffix = ev?.event_visibility === "personal";
  const body = mountPreact(
    <AddParticipantBody
      store={store}
      alreadyIds={alreadyIds}
      currentGuests={currentGuests}
      eventId={eventId}
      canRename={canRename}
      canManage={!!canManage}
      hideGuestSuffix={hideGuestSuffix}
      onReload={() => loadMembers(true)}
    />,
  );
  // 슬림 모드(비호스트 회원)에선 batch 저장 버튼 불필요 — 인라인 PATCH가 즉시 반영.
  const footer = canManage
    ? mountPreact(<AddParticipantFooter store={store} alreadyIds={alreadyIds} onSave={onSave} />)
    : null;

  openSheet(canManage ? "참가자 관리" : "게스트 이름 수정", body, footer, {
    // 인라인 이름 수정 후 X/ESC 로 닫아도 부모(이벤트 상세)가 갱신되도록.
    // onClose를 정의하면 sheetChrome의 dismiss가 기본 닫기를 건너뛰므로,
    // 시트 닫기를 콜백 안에서 명시적으로 트리거해야 한다.
    onClose: () => {
      const dirty = store.get().renameDirty;
      closeSheet();
      if (dirty) rerender();
    },
  });
  if (canManage) loadMembers();
}

// =====================================================================
// openParticipantAvailabilitySheet
// =====================================================================

const STEP_MIN = 5;

function TimeStepper({
  label,
  value,
  resetLabel,
  totalEventMin,
  baseIso,
  direction,
  onChange,
}: {
  label: string;
  value: number;
  resetLabel: string;
  totalEventMin: number;
  baseIso: string;
  direction: "from" | "until";
  onChange: (v: number) => void;
}) {
  const clamp = (v: number) => Math.max(0, Math.min(totalEventMin - STEP_MIN, Math.round(v)));
  const summary = (() => {
    if (direction === "from") {
      return value === 0
        ? `→ ${formatTimeShort(baseIso)} 부터 참가`
        : `→ ${formatTimeShort(shiftIsoByMinutes(baseIso, value))} 부터 참가 (${value}분 늦참)`;
    }
    return value === 0
      ? `→ ${formatTimeShort(baseIso)} 까지 참가`
      : `→ ${formatTimeShort(shiftIsoByMinutes(baseIso, -value))} 까지 참가 (${value}분 조퇴)`;
  })();

  return (
    <Fragment>
      <p class="field__label" style={direction === "until" ? "margin-top:12px" : undefined}>{label}</p>
      <div class="stepper">
        <button type="button" class="stepper__btn" onClick={() => onChange(clamp(value - STEP_MIN))}>−</button>
        <input
          type="number"
          class="stepper__input"
          min="0"
          step={String(STEP_MIN)}
          inputmode="numeric"
          pattern="[0-9]*"
          value={String(value)}
          onChange={(e) => onChange(clamp(parseInt((e.currentTarget as HTMLInputElement).value, 10) || 0))}
        />
        <button type="button" class="stepper__btn" onClick={() => onChange(clamp(value + STEP_MIN))}>+</button>
        <button type="button" class="preset-btn preset-btn--compact" onClick={() => onChange(0)}>{resetLabel}</button>
      </div>
      <p class="availability-summary">{summary}</p>
    </Fragment>
  );
}

function AvailabilitySheet({
  ev,
  p,
  rerender,
  initialFromDelta,
  initialUntilDelta,
}: {
  ev: any;
  p: any;
  rerender: () => void;
  initialFromDelta: number;
  initialUntilDelta: number;
}) {
  const eventStart = ev.court_booking_start_at;
  const eventEnd = ev.court_booking_end_at;
  const totalEventMin = isoMinuteDiff(eventEnd, eventStart);

  const [fromDelta, setFromDelta] = useState(initialFromDelta);
  const [untilDelta, setUntilDelta] = useState(initialUntilDelta);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function onSave() {
    setError(null);
    if (fromDelta + untilDelta >= totalEventMin) {
      setError("참가 시작 시각이 참가 종료 시각 이후예요. 다시 확인해주세요.");
      return;
    }
    setSaving(true);
    const fromIso = fromDelta === 0 ? eventStart : shiftIsoByMinutes(eventStart, fromDelta);
    const untilIso = untilDelta === 0 ? eventEnd : shiftIsoByMinutes(eventEnd, -untilDelta);
    try {
      await api(`/api/v1/events/${ev.event_id}/participants/${p.event_participant_id}`, {
        method: "PATCH",
        body: { available_from_at: fromIso, available_until_at: untilIso },
      });
      toast("참가 시간을 업데이트했어요");
      closeSheet();
      rerender();
    } catch (err: any) {
      setSaving(false);
      setError(err.message || "저장에 실패했어요");
    }
  }

  return (
    <div class="stack">
      <p class="field__hint">
        일정 시간 {formatTimeShort(eventStart)} ~ {formatTimeShort(eventEnd)} 기준으로 참가 시작 / 종료 시각을 조정해요. 해당 라운드 시간에 참가할 수 없는 사람은 대진에서 자동 제외돼요.
      </p>
      <TimeStepper
        label="참가 시작 (늦참)"
        value={fromDelta}
        resetLabel="처음부터"
        totalEventMin={totalEventMin}
        baseIso={eventStart}
        direction="from"
        onChange={setFromDelta}
      />
      <TimeStepper
        label="참가 종료 (조퇴)"
        value={untilDelta}
        resetLabel="끝까지"
        totalEventMin={totalEventMin}
        baseIso={eventEnd}
        direction="until"
        onChange={setUntilDelta}
      />
      {error && <p class="field__error">{error}</p>}
      <button
        class="btn btn--primary btn--block"
        type="button"
        style="margin-top:16px"
        disabled={saving}
        onClick={onSave}
      >
        저장
      </button>
      <button
        class="btn btn--ghost btn--block"
        type="button"
        style="margin-top:8px"
        onClick={() => { setFromDelta(0); setUntilDelta(0); }}
      >
        처음부터 끝까지로 초기화
      </button>
    </div>
  );
}

export async function openParticipantAvailabilitySheet(
  ev: any,
  p: any,
  opts: ParticipantSheetOpts,
): Promise<void> {
  const eventStart = ev.court_booking_start_at;
  const eventEnd = ev.court_booking_end_at;
  if (!eventStart || !eventEnd) {
    toast("일정 시간이 설정되지 않았어요.");
    return;
  }

  const fromDelta = Math.max(0, isoMinuteDiff(p.available_from_at || eventStart, eventStart));
  const untilDelta = Math.max(0, isoMinuteDiff(eventEnd, p.available_until_at || eventEnd));

  const body = mountPreact(
    <AvailabilitySheet
      ev={ev}
      p={p}
      rerender={opts.rerender}
      initialFromDelta={fromDelta}
      initialUntilDelta={untilDelta}
    />,
  );
  const playerName = matchPlayerName(p);
  openSheet(`${playerName !== "-" ? playerName : "참가자"} 시간 조정`, body);
}
